<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { usePageSeo } from '@/composables/usePageSeo'

usePageSeo({
  title: 'Privacy Policy',
  description: 'How Papersnap collects, uses, and protects your data.',
  path: '/privacy',
})

const sections = [
  { id: 'collect', n: '01', label: 'What we collect' },
  { id: 'process', n: '02', label: 'How we process' },
  { id: 'retention', n: '03', label: 'Document retention' },
  { id: 'email', n: '04', label: 'Email delivery' },
  { id: 'payments', n: '05', label: 'Payments' },
  { id: 'analytics', n: '06', label: 'Analytics' },
  { id: 'security', n: '07', label: 'Data security' },
  { id: 'choices', n: '08', label: 'Your choices' },
  { id: 'children', n: '09', label: "Children's privacy" },
  { id: 'changes', n: '10', label: 'Changes' },
  { id: 'contact', n: '11', label: 'Contact us' },
]

const activeId = ref('collect')
let observer = null

onMounted(() => {
  const els = sections.map((s) => document.getElementById(s.id)).filter(Boolean)
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) activeId.value = e.target.id
      })
    },
    { rootMargin: '-90px 0px -60% 0px', threshold: 0 },
  )
  els.forEach((el) => observer.observe(el))
})

onBeforeUnmount(() => observer?.disconnect())

function go(id) {
  const el = document.getElementById(id)
  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 84, behavior: 'smooth' })
}
</script>

<template>
  <section class="privacy-page py-12">
    <div class="priv">
      <!-- Page head -->
      <div class="priv-head">
        <span class="priv-eyebrow">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          Legal
        </span>
        <h1>Privacy Policy</h1>
        <div class="priv-sub">
          <span class="updated-pill">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            Last updated June 15, 2026
          </span>
          <span class="who">Operated by <b>Keelara LLC</b></span>
        </div>
      </div>

      <!-- Lead -->
      <div class="lead">
        <span class="lic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg></span>
        <p>Keelara LLC ("we," "us," or "our") operates Papersnap (<a href="https://www.papersnap.io">www.papersnap.io</a>), an AI-powered document data-extraction service. This Privacy Policy explains what information we handle when you use Papersnap, how we use it, and the choices you have. Unlike some other products, <b>Papersnap requires an account and processes the documents you upload on our servers</b> — this policy describes that processing in detail.</p>
      </div>

      <div class="priv-grid">
        <!-- Sticky TOC -->
        <aside class="toc">
          <p class="toc-label">On this page</p>
          <nav>
            <a
              v-for="s in sections"
              :key="s.id"
              :href="`#${s.id}`"
              :class="{ active: activeId === s.id }"
              @click.prevent="go(s.id)"
            ><span class="tn">{{ s.n }}</span>{{ s.label }}</a>
          </nav>
        </aside>

        <!-- Content -->
        <div class="priv-content">
          <!-- 01 What we collect -->
          <section class="sec" id="collect">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2"/><rect x="7" y="8" width="10" height="8" rx="1"/></svg></span>
              <h2>What we collect</h2>
            </div>
            <div class="collect-list">
              <div class="collect">
                <span class="cic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/><polyline points="22 6 12 13 2 6"/></svg></span>
                <div>
                  <p class="ct">Account information</p>
                  <p class="cd">When you sign up we collect your <b>email address</b>, used for authentication and account-related communication. Authentication is handled by Amazon Cognito; we do not store your password.</p>
                </div>
              </div>
              <div class="collect">
                <span class="cic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></span>
                <div>
                  <p class="ct">Documents you upload</p>
                  <p class="cd">The files you submit for extraction (<b>invoices, receipts, purchase orders, utility bills, financial reports, and similar</b>) and the structured data we extract from them.</p>
                </div>
              </div>
              <div class="collect">
                <span class="cic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg></span>
                <div>
                  <p class="ct">Subscription information</p>
                  <p class="cd">Your plan tier and identifiers from our payment provider (see <b>Payments</b> below). <b>We do not store your card details.</b></p>
                </div>
              </div>
              <div class="collect">
                <span class="cic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg></span>
                <div>
                  <p class="ct">Service telemetry</p>
                  <p class="cd">Standard request logs, error traces, and aggregate usage analytics needed to operate and secure the service.</p>
                </div>
              </div>
            </div>
          </section>

          <!-- 02 How we process -->
          <section class="sec" id="process">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/><circle cx="12" cy="12" r="3"/></svg></span>
              <h2>How we process your documents</h2>
            </div>
            <p>Documents you upload are stored in Amazon S3 with per-user isolation and processed using Amazon Textract and a large language model (Anthropic) to extract structured data. Extracted results are stored in your account (Amazon DynamoDB) so you can view, download, and manage them.</p>
            <div class="keypoint">
              <span class="kic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span>
              <p><b>We do not use your documents or extracted data to train AI models</b>, and we do not sell or share them with advertisers.</p>
            </div>
          </section>

          <!-- 03 Document retention -->
          <section class="sec" id="retention">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l4 2"/></svg></span>
              <h2>Document retention</h2>
            </div>
            <div class="retain">
              <span class="big">30<span>days</span></span>
              <span class="rtxt">Original documents and extracted data are retained in your account for <b>30 days</b>, after which they're automatically deleted.</span>
            </div>
            <p>You can delete a document at any time from the History page; deleted documents are removed within <b>24 hours</b>.</p>
          </section>

          <!-- 04 Email delivery -->
          <section class="sec" id="email">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/><polyline points="22 6 12 13 2 6"/></svg></span>
              <h2>Email delivery <span class="opt">(optional)</span></h2>
            </div>
            <p>If you enable result delivery or the monthly value digest, we send those emails through Amazon SES to your account email address or to a single destination address you configure. You can turn these off at any time in <b>Settings</b>, and every digest includes an unsubscribe link.</p>
          </section>

          <!-- 05 Payments -->
          <section class="sec" id="payments">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg></span>
              <h2>Payments</h2>
            </div>
            <p>Paid subscriptions are processed by our payment provider, Polar (Polar Software Inc.), acting as the merchant of record. When you subscribe, Polar collects and processes your payment and billing information under its own privacy policy. We receive your subscription status and a customer/subscription identifier.</p>
            <div class="keypoint">
              <span class="kic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span>
              <p>We <b>never receive or store your full card number</b>.</p>
            </div>
          </section>

          <!-- 06 Analytics -->
          <section class="sec" id="analytics">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg></span>
              <h2>Analytics</h2>
            </div>
            <p>Papersnap does not use cookies for advertising or cross-site tracking. We keep simple, aggregate counters (such as approximate visitor counts by country, derived from your network connection) to understand how the service is used. These counts are anonymized and not tied to your account or your documents.</p>
          </section>

          <!-- 07 Data security -->
          <section class="sec" id="security">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></span>
              <h2>Data security</h2>
            </div>
            <p>We serve the site over HTTPS. Data held on our servers is stored with a reputable cloud provider (Amazon Web Services), isolated per user, and access is limited to what is needed to operate the service.</p>
          </section>

          <!-- 08 Your choices -->
          <section class="sec" id="choices">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg></span>
              <h2>Your choices</h2>
            </div>
            <div class="choices">
              <div class="choice">
                <span class="chic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></span>
                <span class="ctxt"><b>Delete any document</b> (and its extracted data) from the History page at any time.</span>
              </div>
              <div class="choice">
                <span class="chic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg></span>
                <span class="ctxt"><b>Cancel your subscription</b> at any time from Settings.</span>
              </div>
              <div class="choice">
                <span class="chic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></span>
                <span class="ctxt"><b>Request deletion</b> of your account and associated data by contacting us.</span>
              </div>
            </div>
          </section>

          <!-- 09 Children's privacy -->
          <section class="sec" id="children">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="7" r="4"/><path d="M5.5 21a8 8 0 0 1 13 0"/></svg></span>
              <h2>Children's privacy</h2>
            </div>
            <p>Papersnap is intended for adults and for business use. We do not knowingly collect information from children under the age of 13.</p>
          </section>

          <!-- 10 Changes -->
          <section class="sec" id="changes">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"/></svg></span>
              <h2>Changes to this policy</h2>
            </div>
            <p>We may update this Privacy Policy from time to time. Any changes will be reflected on this page with an updated revision date. We encourage you to review this page periodically.</p>
          </section>

          <!-- 11 Contact -->
          <section class="sec contact" id="contact">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg></span>
              <h2>Contact us</h2>
            </div>
            <p>If you have any questions about this Privacy Policy, you can reach us at:</p>
            <div class="contact-row">
              <a class="mail-btn" href="mailto:contact@keelara.com">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/><polyline points="22 6 12 13 2 6"/></svg>
                contact@keelara.com
              </a>
              <span class="contact-note">Papersnap is operated by <b>Keelara LLC</b>.</span>
            </div>
          </section>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Semantic tokens (light defaults; .dark overrides below). Primary reuses the
   global --color-primary (teal-600), identical to the Claude Design mockup. */
.privacy-page {
  --fg-1: #0f172a; --fg-2: #334155; --fg-3: #475569; --fg-4: #64748b;
  --panel: #ffffff; --sunk: #f8fafc;
  --bd-1: #e2e8f0; --bd-2: #cbd5e1;
  --accent: rgb(var(--color-primary));        /* teal-600 on light */
  --accent-soft: rgb(var(--color-primary) / 0.10);
  --em-bg: rgb(16 185 129 / 0.10); --em-bd: rgb(16 185 129 / 0.30);
  --em-ic-bg: rgb(16 185 129 / 0.16); --em-ic: #059669; --em-fg: #047857;
}
.dark .privacy-page {
  --fg-1: #f8fafc; --fg-2: #cbd5e1; --fg-3: #94a3b8; --fg-4: #64748b;
  --panel: #0f172a; --sunk: rgb(2 6 23 / 0.5);
  --bd-1: #1e293b; --bd-2: #334155;
  --accent: rgb(45 212 191);                   /* teal-400 pops on dark */
  --accent-soft: rgb(45 212 191 / 0.10);
  --em-ic: #34d399; --em-fg: #6ee7b7;
}

.priv { max-width: 980px; margin: 0 auto; width: 100%; }

/* Page head */
.priv-head { margin: 4px 0 26px; }
.priv-eyebrow {
  display: inline-flex; align-items: center; gap: 7px;
  font-family: var(--font-mono); font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--accent); margin-bottom: 12px;
}
.priv-eyebrow svg { width: 13px; height: 13px; }
.priv-head h1 { font-family: var(--font-display); font-weight: 700; font-size: 32px; color: var(--fg-1); margin: 0; letter-spacing: -0.01em; }
.priv-sub { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 12px; }
.updated-pill {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 11px; border-radius: 9999px;
  background: var(--sunk); border: 1px solid var(--bd-2);
  font-size: 12px; font-weight: 600; color: var(--fg-2);
}
.updated-pill svg { width: 12px; height: 12px; color: var(--fg-4); }
.priv-sub .who { font-size: 13px; color: var(--fg-4); }
.priv-sub .who b { color: var(--fg-3); font-weight: 600; }

/* Lead callout */
.lead {
  display: flex; gap: 14px; align-items: flex-start;
  padding: 18px 20px; border-radius: 14px; margin-bottom: 28px;
  background: rgb(var(--color-primary) / 0.07); border: 1px solid rgb(var(--color-primary) / 0.22);
}
.lead .lic { width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0; background: rgb(var(--color-primary) / 0.16); color: var(--accent); display: inline-flex; align-items: center; justify-content: center; }
.lead .lic svg { width: 18px; height: 18px; }
.lead p { margin: 0; font-size: 14px; line-height: 1.65; color: var(--fg-2); }
.lead p a { color: var(--accent); font-weight: 600; }
.lead p b { color: var(--fg-1); font-weight: 600; }

/* Layout: sticky TOC + content */
.priv-grid { display: grid; grid-template-columns: 210px 1fr; gap: 34px; align-items: start; }
@media (max-width: 860px) { .priv-grid { grid-template-columns: 1fr; gap: 18px; } }

.toc { position: sticky; top: 84px; }
@media (max-width: 860px) { .toc { display: none; } }
.toc-label { font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--fg-4); margin: 0 0 10px 12px; }
.toc nav { display: flex; flex-direction: column; gap: 1px; }
.toc a {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 12px; border-radius: 8px; cursor: pointer;
  font-size: 13px; font-weight: 500; color: var(--fg-3);
  border-left: 2px solid transparent; transition: all .15s ease;
}
.toc a:hover { color: var(--fg-1); background: var(--sunk); }
.toc a.active { color: var(--accent); background: var(--accent-soft); border-left-color: rgb(var(--color-primary)); }
.toc a .tn { font-family: var(--font-mono); font-size: 11px; color: var(--fg-4); width: 16px; flex-shrink: 0; }
.toc a.active .tn { color: var(--accent); }

.priv-content { display: flex; flex-direction: column; gap: 16px; min-width: 0; }

/* Section panel */
.sec {
  background: var(--panel); border: 1px solid var(--bd-1); border-radius: 15px;
  padding: 24px 26px; scroll-margin-top: 90px;
}
.sec-head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
.sec-ic { width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0; background: rgb(var(--color-primary) / 0.14); color: var(--accent); display: inline-flex; align-items: center; justify-content: center; }
.sec-ic svg { width: 18px; height: 18px; }
.sec-head h2 { font-family: var(--font-display); font-weight: 600; font-size: 18px; color: var(--fg-1); margin: 0; }
.sec-head h2 .opt { font-size: 13px; font-weight: 500; color: var(--fg-4); }
.sec p { font-size: 14px; line-height: 1.65; color: var(--fg-3); margin: 0 0 12px; }
.sec p:last-child { margin-bottom: 0; }
.sec p b { color: var(--fg-1); font-weight: 600; }

/* "What we collect" rows */
.collect-list { display: flex; flex-direction: column; gap: 10px; }
.collect {
  display: grid; grid-template-columns: 38px 1fr; gap: 14px; align-items: start;
  padding: 14px 16px; border-radius: 12px;
  background: var(--sunk); border: 1px solid var(--bd-1);
}
.collect .cic { width: 38px; height: 38px; border-radius: 10px; background: rgb(var(--color-primary) / 0.12); color: var(--accent); display: inline-flex; align-items: center; justify-content: center; }
.collect .cic svg { width: 18px; height: 18px; }
.collect .ct { font-family: var(--font-display); font-weight: 600; font-size: 14px; color: var(--fg-1); margin: 2px 0 4px; }
.collect .cd { font-size: 13px; line-height: 1.6; color: var(--fg-3); margin: 0; }
.collect .cd b { color: var(--fg-2); font-weight: 600; }

/* Key-point callout */
.keypoint {
  display: flex; gap: 12px; align-items: flex-start;
  margin-top: 14px; padding: 13px 16px; border-radius: 11px;
  background: var(--em-bg); border: 1px solid var(--em-bd);
}
.keypoint .kic { width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0; background: var(--em-ic-bg); color: var(--em-ic); display: inline-flex; align-items: center; justify-content: center; }
.keypoint .kic svg { width: 15px; height: 15px; }
.keypoint p { font-size: 13.5px; line-height: 1.55; color: var(--fg-2); margin: 0; align-self: center; }
.keypoint p b { color: var(--em-fg); font-weight: 700; }

/* Retention highlight */
.retain {
  display: flex; align-items: center; gap: 18px;
  padding: 16px 20px; border-radius: 12px; margin: 4px 0 12px;
  background: var(--sunk); border: 1px solid var(--bd-2);
}
.retain .big { font-family: var(--font-display); font-weight: 700; font-size: 30px; color: var(--fg-1); line-height: 1; }
.retain .big span { font-size: 15px; color: var(--fg-3); font-weight: 600; margin-left: 4px; }
.retain .rtxt { font-size: 13px; line-height: 1.55; color: var(--fg-3); }
.retain .rtxt b { color: var(--fg-1); }

/* Choices list */
.choices { display: flex; flex-direction: column; gap: 8px; }
.choice {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 15px; border-radius: 11px;
  background: var(--sunk); border: 1px solid var(--bd-1);
  transition: border-color .15s ease;
}
.choice:hover { border-color: var(--bd-2); }
.choice .chic { width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0; background: rgb(var(--color-primary) / 0.14); color: var(--accent); display: inline-flex; align-items: center; justify-content: center; }
.choice .chic svg { width: 15px; height: 15px; }
.choice .ctxt { font-size: 13.5px; color: var(--fg-2); }
.choice .ctxt b { color: var(--fg-1); font-weight: 600; }

/* Contact */
.sec.contact { background: linear-gradient(180deg, rgb(var(--color-primary) / 0.06), var(--panel)); border-color: rgb(var(--color-primary) / 0.22); }
.contact-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.mail-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 16px; border-radius: 10px;
  background: rgb(var(--color-primary)); color: #fff;
  font-weight: 600; font-size: 13.5px; transition: background .15s ease;
}
.mail-btn:hover { background: rgb(var(--color-primary) / 0.88); }
.mail-btn svg { width: 15px; height: 15px; }
.contact-note { font-size: 13px; color: var(--fg-4); }
.contact-note b { color: var(--fg-3); font-weight: 600; }
</style>
